import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import React, { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Select } from "@/components/elements/Select";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { useEnum } from "@/api/services/global/enum";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { YES_NO_PENDING_STATUS } from "@/types/enum";
import { Textarea } from "@/components/elements/Textarea";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { black, blue } from "@/theme/colors";
import { ClearIcon } from "@mui/x-date-pickers";
import { createJobSalarySummaryForm } from "@/features/job/utils/convertJobFormData";
import { sanitizeInput } from "@/utils/pieces";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";

type TJobEmploymentConditionProps = {
  isDraft?: boolean;
  bordered?: boolean;
};

export const JobEmploymentCondition: FC<TJobEmploymentConditionProps> = ({ isDraft, bordered = true }) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;
  const { data: employmentTypeOptions } = useEnum("EEmploymentType");
  const { data: salaryTypeOptions } = useEnum("ESalaryType");
  const { data: yesNoPendingStatusOptions } = useEnum("EYesNoPendingStatus");
  const confirmRemove = useDialog();
  const bonusWatchField = useWatch({
    control,
    name: "bonus",
  });

  const salaryRaiseWatchField = useWatch({
    control,
    name: "salaryRaise",
  });

  const jobSalarySummariesWatchField = useWatch({
    control,
    name: "jobSalarySummaries",
  });

  const handleAddJobSalarySummary = () => {
    if (jobSalarySummariesWatchField) {
      setValue("jobSalarySummaries", [...jobSalarySummariesWatchField, createJobSalarySummaryForm()]);
    }
  };

  const handleRemoveJobSalarySummary = async () => {
    if (jobSalarySummariesWatchField && jobSalarySummariesWatchField.length > 1) {
      const lastIndex = jobSalarySummariesWatchField.length - 1;

      await handleRemoveJobSalarySummaryByIndex(lastIndex);
    }
  };

  const hasCompleteJobSalarySummary = (index: number): boolean => {
    const jobSalarySummary = jobSalarySummariesWatchField?.[index];
    if (!jobSalarySummary) return false;

    const { _clientId, ...relevantFields } = jobSalarySummary;

    return Object.values(relevantFields).some((value) => value !== null && value !== undefined && value !== "");
  };

  const removeJobSalarySummary = (index: number): void => {
    if (!jobSalarySummariesWatchField) return;

    methods.unregister(`jobSalarySummaries.${index}`);

    const updatedSummaries = jobSalarySummariesWatchField.filter((_, i) => i !== index);
    setValue("jobSalarySummaries", updatedSummaries, { shouldValidate: true });
  };

  const handleRemoveJobSalarySummaryByIndex = async (index: number): Promise<void> => {
    if (!jobSalarySummariesWatchField || jobSalarySummariesWatchField.length <= 1) {
      return;
    }

    const shouldConfirm = hasCompleteJobSalarySummary(index);

    if (shouldConfirm) {
      const confirmed = await confirmRemove.show();
      if (!confirmed) return;
    }

    removeJobSalarySummary(index);
  };

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="雇用条件" />} showDivider={false} bordered={bordered} padding={!bordered ? 0 : "24px"}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormField control={control} name="businessContent">
              <Textarea />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="employmentType" required={!isDraft}>
              <Select placeholder="選択してください" options={employmentTypeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="salaryType">
              <Select placeholder="選択してください" options={salaryTypeOptions} />
            </FormField>
          </Grid>

          {(jobSalarySummariesWatchField ?? []).map((item, index) => (
            <React.Fragment key={item._clientId ?? `salary-summary-${index}`}>
              <Grid item xs={12}>
                <InnerBox title={`総支給額${index + 1}`} showDivider={false} padding="16px">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormField
                        control={control}
                        label="総支給額"
                        id={`jobSalarySummaries-${index}-totalAmount`}
                        name={`jobSalarySummaries.${index}.totalAmount`}
                        errorMessage={methods.getFieldState(`jobSalarySummaries.${index}.totalAmount`).error?.message}
                        required={!isDraft}
                      >
                        <Input placeholder="入力してください" clearable={false} endAdornment="円" commasFormat={true} />
                      </FormField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        control={control}
                        label="総支給額の内訳"
                        id={`jobSalarySummaries-${index}-totalAmountBreakdown`}
                        name={`jobSalarySummaries.${index}.totalAmountBreakdown`}
                        errorMessage={methods.getFieldState(`jobSalarySummaries.${index}.totalAmountBreakdown`).error?.message}
                      >
                        <Textarea
                          onBlur={(e) => {
                            const sanitized = sanitizeInput(e.target.value);
                            setValue(`jobSalarySummaries.${index}.totalAmountBreakdown`, sanitized);
                          }}
                        />
                      </FormField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        control={control}
                        label="控除金額"
                        id={`jobSalarySummaries-${index}-deductionAmount`}
                        name={`jobSalarySummaries.${index}.deductionAmount`}
                        errorMessage={methods.getFieldState(`jobSalarySummaries.${index}.deductionAmount`).error?.message}
                      >
                        <Input placeholder="入力してください" clearable={false} endAdornment="円" commasFormat={true} />
                      </FormField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        control={control}
                        label="控除内容の詳細"
                        id={`jobSalarySummaries-${index}-deductionDetail`}
                        name={`jobSalarySummaries.${index}.deductionDetail`}
                        errorMessage={methods.getFieldState(`jobSalarySummaries.${index}.deductionDetail`).error?.message}
                      >
                        <Textarea
                          onBlur={(e) => {
                            const sanitized = sanitizeInput(e.target.value);
                            setValue(`jobSalarySummaries.${index}.deductionDetail`, sanitized);
                          }}
                        />
                      </FormField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        control={control}
                        label="目安手取り金額"
                        id={`jobSalarySummaries-${index}-netSalaryEstimate`}
                        name={`jobSalarySummaries.${index}.netSalaryEstimate`}
                        errorMessage={methods.getFieldState(`jobSalarySummaries.${index}.netSalaryEstimate`).error?.message}
                      >
                        <Input placeholder="入力してください" clearable={false} endAdornment="円" commasFormat={true} />
                      </FormField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        control={control}
                        label="手取り金額計算背景"
                        id={`jobSalarySummaries-${index}-netSalaryEstimateNote`}
                        name={`jobSalarySummaries.${index}.netSalaryEstimateNote`}
                        errorMessage={methods.getFieldState(`jobSalarySummaries.${index}.netSalaryEstimateNote`).error?.message}
                      >
                        <Textarea
                          onBlur={(e) => {
                            const sanitized = sanitizeInput(e.target.value);
                            setValue(`jobSalarySummaries.${index}.netSalaryEstimateNote`, sanitized);
                          }}
                        />
                      </FormField>
                    </Grid>
                  </Grid>
                </InnerBox>
              </Grid>
              {jobSalarySummariesWatchField && index + 1 < jobSalarySummariesWatchField.length && (
                <Grid paddingTop="0 !important" item xs={12} mt={1}>
                  <Stack flexDirection="row" alignItems="center" gap={1.5}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleRemoveJobSalarySummaryByIndex(index)}
                    >
                      <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                      <Typography variant="cap12Light">削除</Typography>
                    </Stack>
                  </Stack>
                </Grid>
              )}
            </React.Fragment>
          ))}
          {jobSalarySummariesWatchField && (
            <Grid paddingTop="0 !important" item xs={12} mt={1}>
              <Stack flexDirection="row" alignItems="center" gap={1.5}>
                {jobSalarySummariesWatchField.length < 3 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={handleAddJobSalarySummary}>
                    <AddCircleOutlineIcon
                      sx={{
                        color: blue[80],
                        fontSize: 18,
                      }}
                    />
                    <Typography variant="cap12">総支給額～手取り金額計算背景を追加</Typography>
                  </Stack>
                )}
                {jobSalarySummariesWatchField?.length && jobSalarySummariesWatchField.length > 1 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={handleRemoveJobSalarySummary}>
                    <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                    <Typography variant="cap12Light">削除</Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormField control={control} name="otherAllowanceDescription">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="overtimeDescription">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="bonus" required={!isDraft}>
              <RadioGroup
                direction="row"
                options={yesNoPendingStatusOptions}
                onChange={(value) => {
                  if (value !== YES_NO_PENDING_STATUS.YES) {
                    methods.setValue("bonusInfo", "");
                  }
                }}
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="bonusInfo">
              <Textarea disabled={bonusWatchField !== YES_NO_PENDING_STATUS.YES} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="salaryRaise" required={!isDraft}>
              <RadioGroup
                direction="row"
                options={yesNoPendingStatusOptions}
                onChange={(value) => {
                  if (value !== YES_NO_PENDING_STATUS.YES) {
                    methods.setValue("salaryRaiseTime", "");
                  }
                }}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="salaryRaiseTime">
              <Input placeholder="入力してください" disabled={salaryRaiseWatchField !== YES_NO_PENDING_STATUS.YES} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="employmentConditionNote">
              <Textarea />
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>
      <ConfirmSaveModal title="削除の確認" open={confirmRemove.open} onOk={confirmRemove.confirm} onClose={confirmRemove.cancel}>
        <Typography component="p" variant="subtitle1">
          「総支給額」を削除してもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </Stack>
  );
};
